import {
    BlurEvent, ChangeEvent, DropdownItem, DropdownSelectionEvent, LookupModelSelectionEvent, TableRow, Textbox
} from "@mcleod/components";
import { EdiOrderRates } from "@mcleod/datafusion/src/EdiOrderRates";
import { OrderRates } from "./OrderRates";
import { QuoteRates } from "./QuoteRates";
import { RecOrderRates } from "./RecOrderRates";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { ModelRow, StringUtil } from "@mcleod/core";
import { BillingRates } from "@mcleod/ar/src/BillingRates";
import { LocationByTypeProps, LocationByTypeUtil } from "./LocationByTypeUtil";
import { EdiBillingRates } from "@mcleod/datafusion/src/EdiBillingRates";

const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    cityType: "C",
    stateType: "S",
    locationType: "L",
    countyType: "N",
    useDefault: true,
    lookupProps: {
        city: {
            lookupModelResultField: "full_csz_name"
        },
        state: {
            lookupModelDisplayField: "name"
        }
    }
}

export class RateUtil {
    public static readonly PREPAID = new DropdownItem("P", "Prepaid");
    public static readonly THIRD_PARTY = new DropdownItem("T", "Third party");
    public static readonly COLLECT = new DropdownItem("C", "Collect");
    public static readonly COD = new DropdownItem("D", "C.O.D.");

    public static getCollectionMethodDropdownItems(): DropdownItem[] {
        return [this.PREPAID, this.THIRD_PARTY, this.COLLECT, this.COD];
    }

    public static setRateListeners(layout: OrderRates | RecOrderRates | QuoteRates | EdiOrderRates | BillingRates| EdiBillingRates) {
        RateUtil.addRateFieldUpdateListener(layout);
        layout.textboxRateType.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (event.userInitiatedChange) {
                RateUtil.enableDisableUnitsAndRates(layout, event.selection?.value);
            }
        });
    }

    public static async enableDisableUnitsAndRates(layout: OrderRates | RecOrderRates | QuoteRates | EdiOrderRates | BillingRates, newValue: string, isUpdate: boolean = true, shouldFocus: boolean = true, shouldRate: boolean = true) {
        const isOther = newValue === "Other" || newValue === "O";
        layout.textboxRateUnits.enabled = isOther;
        layout.textboxRateUnitDesc.visible = isOther;

        if (shouldRate) {
            await layout.calculateRates(false, false).then(() => {
                if (isUpdate && shouldFocus) {
                    isOther ? layout.textboxRateUnitDesc.focus() : layout.textboxRate.focus();
                }
            });
        }
    }

    public static setAutoRateContext(layout: OrderRates | RecOrderRates | QuoteRates | EdiOrderRates | BillingRates|EdiBillingRates, rateId: number, odRateId: number) {
        layout.mainDataSource.setComponentsBusy(false);
        if (rateId != null) {
            layout.labelAutoRated.visible = true;
            layout.mainDataSource.activeRow.set("rate_id", rateId);
            if (!(layout instanceof BillingRates)) layout.mainDataSource.activeRow.set("orig_dest_rate_id", odRateId);
            if (odRateId != null) {
                CommonTooltips.setTooltipContentLayout(
                    layout.labelAutoRated,
                    odRateId.toString(),
                    "lme/dispatch/rates/OriginDestinationRateQuickInfo"
                );
                CommonTooltips.setTooltipContentLayout(
                    layout.buttonRate,
                    odRateId.toString(),
                    "lme/dispatch/rates/OriginDestinationRateQuickInfo"
                );
            }
            else {
                CommonTooltips.setTooltipContentLayout(
                    layout.labelAutoRated,
                    rateId.toString(),
                    "lme/dispatch/rates/AutoRateQuickInfo"
                );
                CommonTooltips.setTooltipContentLayout(
                    layout.buttonRate,
                    rateId.toString(),
                    "lme/dispatch/rates/AutoRateQuickInfo"
                );
            }
        }
        else {
            layout.labelAutoRated.visible = false;
            layout.mainDataSource.activeRow.set("rate_id", null);
            if (!(layout instanceof BillingRates)) layout.mainDataSource.activeRow.set("orig_dest_rate_id", null);
            for (let i = layout.tableOtherCharges.rows.length - 1; i >= 0; i--) {
                const row = layout.tableOtherCharges.rows[i];
                if (row?.data?.data["rate_id"] != null) {
                    row.deleteRow();
                }
            }
        }
    }

    private static addRateFieldUpdateListener(layout: OrderRates | RecOrderRates | QuoteRates | EdiOrderRates | BillingRates| EdiBillingRates) {
        const rateFields = ["rate", "rate_type", "rate_units", "weight", "rate_min_weight", "equipment_type_id",
            "bill_distance", "customer_id", "order_type_id", "commodity_id", "tractor_type", "def_move_type", "revenue_code_id", "distance", "commodity", "minimum_weight"];
        layout.mainDataSource.boundComponents.forEach((comp) => {
            if (comp instanceof Textbox && rateFields.includes(comp.field)) {
                if (comp.hasLookupModel()) {
                    comp.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
                        await layout.calculateRates(false, comp.field === "customer_id");
                    });
                }
                else if (comp.hasDropdown()) {
                    comp.addChangeListener(async (event: ChangeEvent) => {
                        if (event.oldValue != event.newValue && layout.labelAutoRated.visible && event.userInitiatedChange) {
                            await layout.calculateRates(false, false);
                        }
                    });
                } else {
                    comp.addBlurListener(async (event: BlurEvent) => {
                        if (event.changedWhileFocused) {
                            await layout.calculateRates(false, comp.field === "bill_distance"); // only update fuel surcharge if bill distance is changed
                        }
                    });
                }
            }
        });
    }

    static setupCountyHandler(typeComponent: Textbox, valueComponent: Textbox, countyComponents: Textbox[], countyType: string) {
        this.toggleCountyFields(typeComponent, valueComponent, countyComponents, countyType);
        typeComponent.addChangeListener((event: ChangeEvent) => {
            this.toggleCountyFields(typeComponent, valueComponent, countyComponents, countyType);
        });
        countyComponents.forEach(c => c.addBlurListener((event: BlurEvent) => this.syncCountComponentsRequired(countyComponents)));
    }

    static syncCountComponentsRequired(countyComponents: Textbox[]) {
        const require = countyComponents.some(c => !StringUtil.isEmptyString(c.text));
        countyComponents.forEach(c => c.required = require);
    }

    static toggleCountyFields(typeComponent: Textbox, valueComponent: Textbox, countyComponents: Textbox[], countyType: string) {
        const item = typeComponent.selectedItem;
        const lookupType = typeof item === "string" ? item : item?.value;
        const showCounty = lookupType === countyType
        valueComponent.visible = !showCounty;
        countyComponents.forEach(c => c.visible = showCounty);

        if (showCounty) {
            valueComponent.clear();
        } else {
            countyComponents.forEach(c => {
                c.clear();
                c.required = false;
            });
        }
    }

    static setCountyValuesIfNeeded(rateModelRow: ModelRow<any>, countyCode: string, typeField: string, cityField: string, stateField: string, valueField: string) {
        const type = rateModelRow.getString(typeField, null);
        if (type === countyCode) {
            const counties = rateModelRow.getString(cityField, '');
            const state = rateModelRow.getString(stateField, '');
            rateModelRow.set(valueField, counties + ', ' + state);
        }
    }
    static isDuplicateRateLane(rows: TableRow[], panelRow: ModelRow, showErrorMessage: boolean = true) {
        let isDuplicate = false;
        for (const row of rows) {
            const currRow = row.data;
            if (currRow.get("id") != panelRow.get("id")) {
                let match = false;
                // Origin settings match?
                if (currRow.getString("origin_type", "") == panelRow.getString("origin_type", "")) {
                    if ("C" == currRow.getString("origin_type", "")) {
                        if (currRow.getString("origin_city_id", "") == panelRow.getString("origin_city_id", "")
                            && currRow.getString("origin_city_name", "") == panelRow.getString("origin_city_name", "")
                            && currRow.getString("origin_state", "") == panelRow.getString("origin_state", "")
                            && currRow.getString("origin_zip", "") == panelRow.getString("origin_zip", "")) {
                            match = true;
                        }
                    } else if ("N" == currRow.getString("origin_type", "")) {
                        // Note, origin_city_name holds the county name when origin_type == N.
                        if (currRow.getString("origin_city_name", "") == panelRow.getString("origin_city_name", "")
                            && currRow.getString("origin_state", "") == panelRow.getString("origin_state", "")) {
                            match = true;
                        }
                    } else if (currRow.getString("origin_value", "") == panelRow.getString("origin_value", "")) {
                        match = true;
                    }
                }
                if (match && currRow.getString("destination_type", "") == panelRow.getString("destination_type", "")) {
                    // both origin and dest must match
                    match = false;

                    if ("C" == currRow.getString("destination_type", "")) {
                        if (currRow.getString("destination_cityid", "") == panelRow.getString("destination_cityid", "")
                            && currRow.getString("destination_city", "") == panelRow.getString("destination_city", "")
                            && currRow.getString("destination_state", "")
                            == panelRow.getString("destination_state", "") && currRow.getString("destination_zip", "")
                            == panelRow.getString("destination_zip", "")) {
                            match = true;
                        }
                    } else if ("N" == currRow.getString("destination_type", "")) {
                        // Note, destination_city_name holds the county name when destination_type == N.
                        if (currRow.getString("destination_city", "") == panelRow.getString("destination_city", "")
                            && currRow.getString("destination_state", "")
                            == panelRow.getString("destination_state", "")) {
                            match = true;
                        }
                    } else if (currRow.getString("destination_value", "") == panelRow.getString("destination_value", "")) {
                        match = true;
                    }

                    if (match && RateUtil.doDateRangesOverlap(currRow.getDate("effective_date", null), currRow.getDate("expiration_date", null),
                                panelRow.getDate("effective_date", null), panelRow.getDate("expiration_date", null))) {
                                    if (showErrorMessage) {
                                    CommonDialogs.showError("A target rate is already defined for this lane/commodity/order type/trailer " +
                                        "type/revenue with overlapping effective and expiration dates.");
                                    }
                                    isDuplicate = true;
                                    break;
                    }
                }
            }
        }
        return isDuplicate;
    }

    static areValidDates(effective: Date, expiration: Date, showErrorMessage: boolean = true) {
        if (effective > expiration) {
            if (showErrorMessage) {
                CommonDialogs.showError("Effective date must be before expiration date.");
            }
            return false;
        }
        return true;
    }

    static doDateRangesOverlap(effective1: Date, expiration1: Date, effective2: Date, expiration2: Date) {
        // Handle null values
        if ((effective1 == null && expiration1 == null) || (effective2 == null && expiration2 == null)) {
            return true; // If either range is completely null, it always overlaps with the other
        }
        const arrOfDates = [
            effective1,
            expiration1,
            effective2,
            expiration2
        ]
        const sortedDates = arrOfDates.sort((a, b) => a.getTime() - b.getTime());

        if (expiration1 == null) {
            expiration1 = sortedDates[sortedDates.length-1]; // Far future
        }
        if (expiration2 == null) {
            expiration2 = sortedDates[sortedDates.length-1]; // Far future
        }

        // If only the effective date is null, assume it starts from the beginning of time
        if (effective1 == null) {
            effective1 = sortedDates[0]; // Far past
        }
        if (effective2 == null) {
            effective2 = sortedDates[0]; // Far past
        }
        // Check for overlap
        return !(expiration1 < effective2 || expiration2 < effective1);
    }

    static syncLocationByType(tableRow: TableRow, suffix: String = "") {
        const modelRow = tableRow.data;
        const textboxOrigin = tableRow.findComponentById('textboxOrigin' + suffix) as Textbox;
        const textboxIntermediate = tableRow.findComponentById('textboxIntermediate' + suffix) as Textbox;
        const textboxDestination = tableRow.findComponentById('textboxDestination' + suffix) as Textbox;
        const textboxQuickOrgin = tableRow.findComponentById('textboxQuickOrgin' + suffix) as Textbox;
        const textboxQuickIntermediate = tableRow.findComponentById('textboxQuickInter' + suffix) as Textbox;
        const textboxQuickDestination = tableRow.findComponentById('textboxQuickDest' + suffix) as Textbox;
        if (!modelRow.isNull('orig_code') && textboxQuickOrgin != null && textboxOrigin != null) {
            if (modelRow.isNull('orig_location_name')) {
                const originType = modelRow.getString('orig_code');
                LocationByTypeUtil.setLookupByType(originType, textboxOrigin, false, LOCATION_BY_TYPE_PROPS);
                textboxQuickOrgin.visible = false;
                textboxOrigin.visible = true;
            }
            else {
                textboxQuickOrgin.visible = true;
                textboxOrigin.visible = false;
            }
        }
        if (!modelRow.isNull('intermediate_value') && !modelRow.get('is_multistop') && textboxQuickIntermediate != null && textboxIntermediate != null) {
            if (modelRow.isNull('inter_location_name')) {
                const intermediateType = modelRow.getString('intermediate_code');
                LocationByTypeUtil.setLookupByType(intermediateType, textboxIntermediate, false, LOCATION_BY_TYPE_PROPS);
                textboxQuickIntermediate.visible = false;
                textboxIntermediate.visible = true;
            }
            else {
                textboxQuickIntermediate.visible = true;
                textboxIntermediate.visible = false;
            }
        }
        if (!modelRow.isNull('dest_code') && textboxQuickDestination != null && textboxDestination != null) {
            if (modelRow.isNull('dest_location_name')) {
                const destinationType = modelRow.getString('dest_code', null);
                LocationByTypeUtil.setLookupByType(destinationType, textboxDestination, false, LOCATION_BY_TYPE_PROPS);
                textboxQuickDestination.visible = false;
                textboxDestination.visible = true;
            }
            else {
                textboxQuickDestination.visible = true;
                textboxDestination.visible = false;
            }
        }
    }

    static syncRate(tableRow: TableRow, suffix: string = "") {
        const modelRow = tableRow.data;
        const textboxRate = tableRow.findComponentById('textboxRate') as Textbox;
        if (textboxRate == null) return;
        if (modelRow?.getString('rate_type') === 'R') { //if method is rate table, disallow editing rate inline
            textboxRate.printableDuringUpdate = true;
        }
        else {
            textboxRate.printableDuringUpdate = false;
        }
    }
}
